import { AndroidMockup, IPhoneMockup } from 'react-device-mockup';
import { getScreenCornerRadius } from '@/pages/onboarding/mockup/iphone-mockup';
import { IPhoneStatusBar } from '@/pages/onboarding/mockup/iphone-status-bar';

export type DevicePlatform = 'ios' | 'android';

export interface DeviceFrameProps {
  platform?: DevicePlatform;
  screenWidth: number;
  /** Colour of the iPhone status bar. */
  theme?: 'light' | 'dark';
  /** The app screen; fills the device screen. */
  children: React.ReactNode;
}

// The status bar is calibrated at the onboarding mockup's default screen width
// (fixed 12px clock and insets); it is scaled with the screen so the clock and
// the icons keep clear of the Dynamic Island at any canvas size.
const STATUS_BAR_REFERENCE_WIDTH = 300;

// Near-black so the Dynamic Island reads as a cutout (same as the onboarding).
const FRAME_COLOR = '#1c1c1e';

/** The only place the editors draw a device (AD-005, AD-028). */
export function DeviceFrame({
  platform = 'ios',
  screenWidth,
  theme = 'light',
  children,
}: DeviceFrameProps) {
  const screen = (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ borderRadius: getScreenCornerRadius(screenWidth) }}
    >
      {children}
      {platform === 'ios' && (
        <div
          className="pointer-events-none absolute left-0 top-0 origin-top-left"
          style={{
            width: STATUS_BAR_REFERENCE_WIDTH,
            transform: `scale(${screenWidth / STATUS_BAR_REFERENCE_WIDTH})`,
          }}
        >
          <IPhoneStatusBar theme={theme} />
        </div>
      )}
    </div>
  );

  return (
    <div data-platform={platform} className="flex justify-center">
      {platform === 'ios' ? (
        <IPhoneMockup
          screenWidth={screenWidth}
          screenType="island"
          hideStatusBar
          hideNavBar
          frameColor={FRAME_COLOR}
        >
          {screen}
        </IPhoneMockup>
      ) : (
        <AndroidMockup
          screenWidth={screenWidth}
          hideStatusBar
          hideNavBar
          frameColor={FRAME_COLOR}
        >
          {screen}
        </AndroidMockup>
      )}
    </div>
  );
}
